<template>
    <div id="app" class="min-w-[1024px] min-h-[100vh] flex">
        <AppAside />
        <AppMain :header-name="titleText">
            <div class="flex-1 mt-9 flex flex-col">
                <div class="px-10 pt-10 rounded-02 w-full max-w-7xl 2xl:w-3/4 bg-background-basic-1 border border-content-basic-4">
                    <div class="flex flex-col gap-2">
                        <div class="font-inter-heading-18 text-content-basic-1">Wallet Name</div>
                        <ElementsInput
                            v-model="walletCategory.walletCategoryName"
                            placeholder="Please enter the wallet name"
                            :error="walletCategoryError.walletCategoryName"
                            :showErrorMessage="false"
                            :maxlength="30"
                            height11
                            full
                        />
                        <div class="flex items-center justify-between">
                            <p class="font-inter-caption-2 text-accent-red-3">
                                {{ walletCategoryError.walletCategoryName }}
                            </p>
                            <div class="font-inter-caption-2 text-right text-content-basic-3">
                                ({{ (walletCategory.walletCategoryName || '').length }}/30)
                            </div>
                        </div>
                    </div>

                    <div class="mt-6 flex flex-col gap-2">
                        <div class="font-inter-heading-18 text-content-basic-1">Description</div>
                        <ElementsInput
                            v-model="walletCategory.description"
                            placeholder="Please enter the wallet description"
                            :error="walletCategoryError.description"
                            :showErrorMessage="false"
                            :maxlength="90"
                            height11
                            full
                        />
                        <div class="flex items-center justify-between">
                            <p class="font-inter-caption-2 text-accent-red-3">
                                {{ walletCategoryError.description }}
                            </p>
                            <div class="font-inter-caption-2 text-right text-content-basic-3">
                                ({{ (walletCategory.description || '').length }}/90)
                            </div>
                        </div>
                    </div>

                    <div class="my-10 w-full h-px bg-background-basic-3"></div>

                    <div class="flex flex-col">
                        <div class="flex flex-col items-start">
                            <div class="font-inter-heading-18 text-content-basic-1">
                                Category ({{ (walletCategory.allowedFlexbenCategoryList || []).length }})
                            </div>
                            <div class="font-inter-body-3 text-gray-500">
                                Select a category to assign to this wallet.
                            </div>
                        </div>

                        <div class="mt-5 grid grid-cols-5 gap-8">
                            <button
                                v-for="c in flexbenCategoryList"
                                :key="c.flexbenCategorySeq"
                                type="button"
                                @click="toggleCategory(c.flexbenCategorySeq)"
                                class="flex flex-col items-center justify-center gap-2"
                            >
                                <div
                                    :class="[
                                        'w-[84px] h-[84px] rounded-full border flex items-center justify-center overflow-hidden',
                                        isSelected(c.flexbenCategorySeq) ? 'border-primary-3' : 'border-gray-500',
                                    ]"
                                >
                                    <img 
                                        :src="require(`@/assets/img/categories/${c.categoryImagePath}`)" 
                                        :alt="c.categoryName"
                                        :class="[
                                            'w-full h-full object-cover',
                                            isSelected(c.flexbenCategorySeq) ? '' : 'grayscale opacity-50'
                                        ]"
                                    />
                                </div>
                                <div 
                                    :class="['font-inter-heading-20 leading-[1.5] text-center h-12 line-clamp-2', isSelected(c.flexbenCategorySeq) ? 'text-primary-3' : 'text-gray-500']"
                                    v-html="c.categoryName"
                                ></div>
                            </button>
                        </div>
                        <p class="font-inter-caption-2 text-accent-red-3">
                            {{ walletCategoryError.allowedFlexbenCategoryList }}
                        </p>
                    </div>
                    <div class="mt-6 -mx-10 px-10 flex justify-end items-center gap-3 py-6 border-t">
                        <DesignButton class="w-24" btnType="QUATERNARY" btnSize="MD" :btnDisabled="false" btnText="Cancel" @click-event="onCancel" />
                        <DesignButton class="w-24" btnType="PRIMARY" btnSize="MD" :btnDisabled="false" :btnText="primaryButtonText" @click-event="onSubmit" />
                    </div>
                </div>
            </div>
        </AppMain>
    </div>
</template>

<style scoped>
.blu {
    background: #4361ee;
}
.blu:hover {
    background: #334cba;
}
</style>

<script>
import AppAside from '@/components/AppAside.vue';
import AppMain from '@/components/main/AppMain.vue';
import ElementsInput from '@/components/elements/ElementsInput.vue';
import DesignButton from '@/components/design-system/design-button.vue';

export default {
    components: {
        AppAside,
        AppMain,
        ElementsInput,
        DesignButton,
    },
    data() {
        return {
            editWalletCategorySeq: null,
            walletCategory: {
                walletCategoryName: null,
                description: null,
                allowedFlexbenCategoryList: [],
            },
            walletCategoryError: {
                walletCategoryName: null,
                description: null,
                allowedFlexbenCategoryList: null,
            },
            flexbenCategoryList: [],
        };
    },
    computed: {
        isEdit() {
            return !!this.editWalletCategorySeq;
        },
        titleText() {
            return this.isEdit ? 'Edit Wallet' : 'Add Wallet';
        },
        primaryButtonText() {
            return this.isEdit ? 'Save' : 'Add';
        },
    },
    mounted() {
        const url = new URL(window.location);
        this.editWalletCategorySeq = url.searchParams.get('walletCategorySeq') ?? null;
        
        this.getFlexbenCategoryList();
        if (this.isEdit) {
            this.getWalletCategory();
        }
    },
    methods: {
        getFlexbenCategoryList() {
            const self = this;
            const url = self.$api('uri', 'get-flexben-category-list');
            self.$axios
            .get(url)
            .then(res => { 
                self.flexbenCategoryList = res.data.data;
            })
            .catch(err => { 
                console.error('err : ', err); 
            });
        },
        getWalletCategory() {
            const self = this;
            const url = self.$api('uri', 'get-wallet-category').replace('{walletCategorySeq}', self.editWalletCategorySeq);

            self.$axios
                .get(url)
                .then((res) => {
                    self.walletCategory = res.data.data;
                })
                .catch((err) => {
                    console.error('err ? : ', err);
                });
        },
        toggleCategory(id) {
            const self = this;
            const existingIndex = self.walletCategory.allowedFlexbenCategoryList.findIndex(category => category.flexbenCategorySeq === id);
            
            if (existingIndex !== -1) {
                // 이미 선택된 경우: 제거
                self.walletCategory.allowedFlexbenCategoryList.splice(existingIndex, 1);
            } else {
                // 선택되지 않은 경우: 추가
                const categoryToAdd = self.flexbenCategoryList.find(category => category.flexbenCategorySeq === id);
                if (categoryToAdd) {
                    self.walletCategory.allowedFlexbenCategoryList.push(categoryToAdd);
                }
            }
        },
        isSelected(id) {
            return this.walletCategory.allowedFlexbenCategoryList.some(category => category.flexbenCategorySeq === id);
        },
        onCancel() {
            window.history.back();
        },
        onSubmit() {
            const self = this;
            const payload = {
                walletCategoryName: (self.walletCategory.walletCategoryName || '').trim(),
                description: self.walletCategory.description || '',
                flexbenCategorySeqs: (self.walletCategory.allowedFlexbenCategoryList || []).map((item) =>
                    typeof item === 'object' ? item.flexbenCategorySeq : item
                ),
            };
            const isError = self.validateWalletCategory(payload);
            if (isError) {
                return;
            }

            if (self.isEdit) {
                self.updateWalletCategory(payload);
            } else {
                self.addWalletCategory(payload);
            }
        },
        validateWalletCategory(payload) {
            const self = this;
            self.initWalletCategoryError();

            let isError = false;
            if (!payload.walletCategoryName) {
                self.walletCategoryError.walletCategoryName = 'This field is required.';
                isError = true;
            }

            if (!payload.description) {
                self.walletCategoryError.description = 'This field is required.';
                isError = true;
            }

            if (payload.flexbenCategorySeqs.length === 0) {
                self.walletCategoryError.allowedFlexbenCategoryList = 'At least one category must be selected.';
                isError = true;
            }

            return isError;
        },
        initWalletCategoryError() {
            const self = this;
            self.walletCategoryError.walletCategoryName = '';
            self.walletCategoryError.description = '';
            self.walletCategoryError.allowedFlexbenCategoryList = '';
        },
        updateWalletCategory(payload) {
            const self = this;
            const url = self.$api('uri', 'put-wallet-category').replace('{walletCategorySeq}', self.editWalletCategorySeq);
            self.$axios
                .put(url, payload)
                    .then(() => {
                        self.redirectToWalletSetting();
                    })
                .catch((err) => {
                    const code = err?.response?.data?.code;
                    if (code === 'HR_WALLET_CATEGORY_NAME_DUPLICATED') {
                        self.walletCategoryError.walletCategoryName = 'Duplicated wallet Name. Please check it again.';
                    }
                });
        },
        addWalletCategory(payload) {
            const self = this;
            const url = self.$api('uri', 'post-wallet-category');
            self.$axios
                .post(url, payload)
                    .then(() => {
                        self.redirectToWalletSetting();
                    })
                .catch((err) => {
                    const code = err?.response?.data?.code;
                    if (code === 'HR_WALLET_CATEGORY_MAX_LIMIT_EXCEEDED') {
                        alert('Up to 5 wallets can be created.');
                        return;
                    }

                    if (code === 'HR_WALLET_CATEGORY_NAME_DUPLICATED') {
                        self.walletCategoryError.walletCategoryName = 'Wallet name already exists.';
                        return;
                    }
                });
        },
        redirectToWalletSetting() {
            // shell인 경우 shell 페이지로 이동, 아닌 경우 기존 페이지로 이동
            const path = this.$appUtil.checkPermission('FLEXBEN_EDIT_SHELL')
                ? '/flex_ben_shell/flex_ben_setting'
                : '/flex_ben/flex_ben_setting';
            window.location.href = `${path}?tab=walletSetting`;
        },
    },
};
</script>


